import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import esES from 'date-fns/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  MenuItem, 
  TextField, 
  Grid, 
  Alert, 
  Box 
} from '@mui/material';
import { ChromePicker } from 'react-color';

const locales = { 'es': esES };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const CalendarioAsignacionTenido = () => {
  const [events, setEvents] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [referencias, setReferencias] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    machineNumber: '',
    referencia: '',
    cantidad: '',
    nota: '',
    color: '#348125ff',
    fechaInicio: '',
    horaInicio: '07:00',
    fechaFin: '',
    horaFin: '15:00'
  });
  const [editingEvent, setEditingEvent] = useState(null);
  const [error, setError] = useState(null);
  const [date, setDate] = useState(new Date());
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Función para verificar conflictos de horarios en la misma máquina
  const checkEventConflict = useCallback((newEvent) => {
    const { machineNumber, fechaInicio, fechaFin, id } = newEvent;
    const newStart = new Date(fechaInicio);
    const newEnd = new Date(fechaFin);

    // Normalizar machineNumber para comparación
    const normalizedNewMachine = String(machineNumber).toLowerCase().replace(/^machine/, '');

    // Buscar eventos de la misma máquina que no sean el evento actual (en caso de edición)
    const conflictingEvents = events.filter(event => {
      if (id && event.id === id) return false; // Ignorar el evento que estamos editando
      
      // Normalizar machineNumber del evento para comparación
      const normalizedEventMachine = String(event.raw.machineNumber).toLowerCase().replace(/^machine/, '');
      if (normalizedEventMachine !== normalizedNewMachine) return false; // Solo misma máquina

      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);

      // Verificar solapamiento: 
      // Nuevo evento comienza antes de que termine el existente Y termina después de que comience el existente
      return (newStart < eventEnd && newEnd > eventStart);
    });

    return conflictingEvents;
  }, [events]);

  // Callback para prevenir selecciones que creen conflictos
  const handleSelecting = useCallback(() => {
    // Si estamos editando, permitir la selección
    if (editingEvent) return true;
    
    // En modo creación, no validamos hasta que se seleccione la máquina
    return true;
  }, [editingEvent]);

  // Cargar datos iniciales
  const fetchInitialData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const [machinesRes, referenciasRes, asignacionesRes, mantenimientosRes] = await Promise.all([
        fetch('/api/machines', { headers }),
        fetch('/api/referencias', { headers }),
        fetch('/api/asignar-tenido', { headers }),
        fetch('/api/mantenimiento', { headers })
      ]);
      const [machinesData, referenciasData, asignacionesData, mantenimientosData] = await Promise.all([
        machinesRes.json(),
        referenciasRes.json(),
        asignacionesRes.json(),
        mantenimientosRes.json()
      ]);
      
      // Filtrar solo las máquinas 9, 10 y 11
      const maquinasTenido = Array.isArray(machinesData) 
        ? machinesData.filter(m => {
            const num = String(m.machineNumber).toLowerCase();
            return num === 'machine9' || num === 'machine10' || num === 'machine11';
          })
        : [];
      
      setMaquinas(maquinasTenido);
      setReferencias(Array.isArray(referenciasData) ? referenciasData : []);
      
      // Mapear asignaciones a eventos del calendario
      const mappedEvents = (Array.isArray(asignacionesData) ? asignacionesData : []).map(asig => {
        const start = new Date(asig.fechaInicio);
        const end = new Date(asig.fechaFin);
        const horaInicio = format(start, 'HH:mm');
        const horaFin = format(end, 'HH:mm');
        return {
          id: asig._id,
          title: `M${asig.machineNumber} | ${asig.referencia} | ${asig.cantidad}kg | ${horaInicio}-${horaFin}`,
          start,
          end,
          raw: asig,
          color: asig.color || '#348125ff',
          allDay: false,
          tipo: 'asignacion'
        };
      });

      // Mapear mantenimientos como eventos bloqueados (solo las máquinas 9, 10, 11)
      const maquinasTeñidoNumbers = ['9', '10', '11'];
      const mantenimientosEvents = (Array.isArray(mantenimientosData) ? mantenimientosData : [])
        .filter(mant => {
          const machineNum = String(mant.machineNumber).toLowerCase().replace(/^machine/, '');
          return maquinasTeñidoNumbers.includes(machineNum) && 
                 mant.estado !== 'cancelado';
        })
        .map(mant => {
          const start = new Date(mant.fechaInicio);
          const end = new Date(mant.fechaFin);
          const horaInicio = format(start, 'HH:mm');
          const horaFin = format(end, 'HH:mm');
          
          return {
            id: `mant-${mant._id}`,
            title: `🔧 MANTENIMIENTO M${mant.machineNumber} | ${mant.tipoMantenimiento.toUpperCase()} | ${horaInicio}-${horaFin}`,
            start,
            end,
            raw: mant,
            color: '#dc2626', // Rojo para mantenimientos
            allDay: false,
            tipo: 'mantenimiento'
          };
        });

      setEvents([...mappedEvents, ...mantenimientosEvents]);
    } catch (err) {
      setError("No se pudieron cargar los datos iniciales.");
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const handleSelectSlot = ({ start, end }) => {
    // Capturar fecha y hora del slot seleccionado
    const fechaInicio = format(start, 'yyyy-MM-dd');
    const horaInicio = format(start, 'HH:mm');
    const fechaFin = format(end, 'yyyy-MM-dd');
    const horaFin = format(end, 'HH:mm');

    setForm({
      machineNumber: '',
      referencia: '',
      cantidad: '',
      nota: '',
      color: '#348125ff',
      fechaInicio,
      horaInicio,
      fechaFin,
      horaFin
    });
    setEditingEvent(null);
    setError(null);
    setOpen(true);
  };

  const handleSelectEvent = (event) => {
    // Si es un evento de mantenimiento, mostrar alerta y no abrir el diálogo
    if (event.tipo === 'mantenimiento') {
      setError('Este es un evento de mantenimiento. Debe editarse desde el Calendario de Mantenimiento.');
      setTimeout(() => setError(null), 5000);
      return;
    }

    const fechaInicio = format(new Date(event.raw.fechaInicio), 'yyyy-MM-dd');
    const horaInicio = format(new Date(event.raw.fechaInicio), 'HH:mm');
    const fechaFin = format(new Date(event.raw.fechaFin), 'yyyy-MM-dd');
    const horaFin = format(new Date(event.raw.fechaFin), 'HH:mm');
    
    setForm({
      machineNumber: event.raw.machineNumber,
      referencia: event.raw.referencia,
      cantidad: event.raw.cantidad,
      nota: event.raw.nota || '',
      color: event.raw.color || '#348125ff',
      fechaInicio,
      horaInicio,
      fechaFin,
      horaFin
    });
    setEditingEvent(event);
    setError(null);
    setOpen(true);
  };

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    
    // Si cambia la máquina, fecha u hora, limpiar el error
    if (['machineNumber', 'fechaInicio', 'fechaFin', 'horaInicio', 'horaFin'].includes(field)) {
      setError(null);
    }
  };

  const handleColorChange = (color) => {
    setForm({ ...form, color: color.hex });
  };

  const handleBuscarFecha = (fecha) => {
    setDate(fecha);
  };

  const handleSave = async () => {
    setError(null);
    if (!form.machineNumber || !form.referencia || !form.cantidad || !form.fechaInicio || !form.fechaFin || !form.horaInicio || !form.horaFin) {
      setError("Completa todos los campos.");
      return;
    }

    // Combinar fecha y hora para crear el timestamp completo
    const fechaInicio = `${form.fechaInicio}T${form.horaInicio}:00`;
    const fechaFin = `${form.fechaFin}T${form.horaFin}:00`;

    // Validar que la fecha de fin sea posterior a la fecha de inicio
    if (new Date(fechaFin) <= new Date(fechaInicio)) {
      setError("La fecha de fin debe ser posterior a la fecha de inicio.");
      return;
    }

    // Verificar conflictos de horarios en la misma máquina
    const conflicts = checkEventConflict({
      machineNumber: form.machineNumber,
      fechaInicio,
      fechaFin,
      id: editingEvent?.id
    });

    if (conflicts.length > 0) {
      const conflictInfo = conflicts.map(c => {
        const start = format(new Date(c.start), 'dd/MM/yyyy HH:mm');
        const end = format(new Date(c.end), 'dd/MM/yyyy HH:mm');
        return `${c.raw.referencia} (${start} - ${end})`;
      }).join(', ');
      
      setError(`Conflicto de horarios con: ${conflictInfo}. La máquina ${form.machineNumber} ya tiene eventos en ese horario.`);
      return;
    }

    const datos = {
      machineNumber: form.machineNumber,
      referencia: form.referencia,
      cantidad: parseFloat(form.cantidad),
      nota: form.nota,
      color: form.color,
      fechaInicio,
      fechaFin
    };

    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    try {
      let response;
      if (editingEvent) {
        response = await fetch(`/api/asignar-tenido/${editingEvent.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(datos)
        });
      } else {
        response = await fetch('/api/asignar-tenido', {
          method: 'POST',
          headers,
          body: JSON.stringify(datos)
        });
      }

      if (response.ok) {
        setOpen(false);
        setShowColorPicker(false);
        fetchInitialData();
      } else {
        const errorData = await response.json();
        setError(`Error al guardar: ${errorData.message || 'Error desconocido'}`);
      }
    } catch (err) {
      setError("Error de conexión al servidor.");
    }
  };

  const handleDelete = async () => {
    setError(null);
    if (!editingEvent) return;

    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

    try {
      const response = await fetch(`/api/asignar-tenido/${editingEvent.id}`, {
        method: 'DELETE',
        headers
      });
      if (response.ok) {
        setOpen(false);
        setShowColorPicker(false);
        fetchInitialData();
      } else {
        const errorData = await response.json();
        setError(`Error al eliminar: ${errorData.message || 'Error desconocido'}`);
      }
    } catch (err) {
      setError("Error de conexión al servidor al eliminar.");
    }
  };

  return (
    <div style={{ height: 900, margin: 20 }}>
      {/* Alerta global para errores de selección */}
      {error && !open && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Componente de búsqueda de fecha */}
      <div style={{ marginBottom: 20 }}>
        <TextField
          label="Buscar fecha"
          type="date"
          onChange={(e) => handleBuscarFecha(new Date(e.target.value))}
          InputLabelProps={{ shrink: true }}
        />
      </div>
      <Calendar
        localizer={localizer}
        culture='es'
        events={events}
        startAccessor="start"
        endAccessor="end"
        selectable
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        onSelecting={handleSelecting}
        views={['month', 'week', 'day', 'agenda']}
        defaultView="day"
        style={{ background: 'rgb(69, 69, 71)', borderRadius: 8 }}
        date={date}
        onNavigate={(newDate) => setDate(newDate)}
        step={15}
        timeslots={4}
        min={new Date(2026, 0, 1, 6, 0, 0)}
        max={new Date(2026, 0, 1, 23, 0, 0)}
        formats={{
          timeGutterFormat: 'HH:mm',
          dayFormat: (date, culture, localizer) => localizer.format(date, 'eeee dd', culture),
          dayHeaderFormat: (date, culture, localizer) => localizer.format(date, 'eeee dd/MM', culture),
          dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'dd MMM', culture)} - ${localizer.format(end, 'dd MMM yyyy', culture)}`,
          monthHeaderFormat: (date, culture, localizer) => localizer.format(date, 'MMMM yyyy', culture),
          agendaDateFormat: (date, culture, localizer) => localizer.format(date, 'eeee dd/MM', culture),
          agendaHeaderFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'dd MMM yyyy', culture)} - ${localizer.format(end, 'dd MMM yyyy', culture)}`,
          eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,
          agendaTimeRangeFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,
        }}
        eventPropGetter={event => ({
          className: 'evento-tenido',
          style: {
            backgroundColor: event.color,
            color: '#fff',
            borderRadius: '6px',
            border: 'none'
          }
        })}
        messages={{
          date: 'Fecha',
          time: 'Hora',
          event: 'Evento',
          allDay: 'Todo el día',
          week: 'Semana',
          work_week: 'Semana laboral',
          day: 'Día',
          month: 'Mes',
          previous: 'Anterior',
          next: 'Siguiente',
          yesterday: 'Ayer',
          tomorrow: 'Mañana',
          today: 'Hoy',
          agenda: 'Agenda',
          noEventsInRange: 'No hay eventos en este rango',
          showMore: total => `+${total} más`
        }}
      />

      <Dialog open={open} onClose={() => { setOpen(false); setShowColorPicker(false); }} container={document.body}>
        <DialogTitle>{editingEvent ? 'Editar asignación de teñido' : 'Nueva asignación de teñido'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          {/* Advertencia de validación en tiempo real */}
          {form.machineNumber && form.fechaInicio && form.horaInicio && form.fechaFin && form.horaFin && (() => {
            const conflicts = checkEventConflict({
              machineNumber: form.machineNumber,
              fechaInicio: `${form.fechaInicio}T${form.horaInicio}:00`,
              fechaFin: `${form.fechaFin}T${form.horaFin}:00`,
              id: editingEvent?.id
            });
            
            if (conflicts.length > 0) {
              const conflictInfo = conflicts.map(c => {
                const start = format(new Date(c.start), 'dd/MM HH:mm');
                const end = format(new Date(c.end), 'HH:mm');
                return `${c.raw.referencia} (${start}-${end})`;
              }).join(', ');
              
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  ⚠️ Conflicto detectado con: {conflictInfo}
                </Alert>
              );
            }
            return null;
          })()}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                label="Máquina"
                value={form.machineNumber}
                onChange={e => handleChange('machineNumber', e.target.value)}
                fullWidth
                required
                MenuProps={{ disablePortal: true }}
              >
                <MenuItem value=""><em>Seleccione una máquina</em></MenuItem>
                {maquinas.map(m => (
                  <MenuItem key={m._id} value={m.machineNumber}>Máquina {m.machineNumber}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Referencia"
                value={form.referencia}
                onChange={e => handleChange('referencia', e.target.value)}
                fullWidth
                required
                MenuProps={{ disablePortal: true }}
              >
                <MenuItem value=""><em>Seleccione una referencia</em></MenuItem>
                {referencias.map(r => (
                  <MenuItem key={r._id} value={r.referencia}>{r.referencia}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Cantidad (kg)"
                type="number"
                value={form.cantidad}
                onChange={e => handleChange('cantidad', e.target.value)}
                fullWidth
                required
                inputProps={{ step: "0.01", min: "0" }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Nota"
                value={form.nota}
                onChange={e => handleChange('nota', e.target.value)}
                fullWidth
                multiline
                rows={2}
                placeholder="Agregar notas o comentarios (opcional)"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  sx={{ flex: 1 }}
                >
                  {showColorPicker ? 'Cerrar selector de color' : 'Seleccionar color'}
                </Button>
                <Box sx={{
                  width: 50, 
                  height: 40, 
                  backgroundColor: form.color,
                  borderRadius: '4px', 
                  border: '2px solid #ccc'
                }} />
              </Box>
              {showColorPicker && (
                <Box sx={{ mt: 2 }}>
                  <ChromePicker 
                    color={form.color}
                    onChange={handleColorChange}
                    disableAlpha
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de inicio"
                type="date"
                value={form.fechaInicio}
                onChange={e => handleChange('fechaInicio', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Hora de inicio"
                type="time"
                value={form.horaInicio}
                onChange={e => handleChange('horaInicio', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de fin"
                type="date"
                value={form.fechaFin}
                onChange={e => handleChange('fechaFin', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Hora de fin"
                type="time"
                value={form.horaFin}
                onChange={e => handleChange('horaFin', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {editingEvent && (
            <Button color="error" onClick={handleDelete}>Eliminar</Button>
          )}
          <Button onClick={() => { setOpen(false); setShowColorPicker(false); }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CalendarioAsignacionTenido;
